<template>
  <v-dialog v-model="Previewdialog" max-width="640" persistent>
    <v-card class="pa-4 preview-card">
      <!-- Header -->
      <div class="d-flex justify-space-between align-center mb-3">
        <div>
          <div class="previewHeading">Event Preview</div>
          <div class="event-id">Event ID: #{{ previewDetails.Id }}</div>
        </div>
        <v-btn icon @click="$emit('update:Previewdialog', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Tags / Metadata Section -->
      <v-card outlined class="mb-4 pa-3 info-section">
        <div class="meta-row">
          <span class="meta-title">Modality:</span>
          <span v-for="(item, idx) in getArray(previewDetails.Modality)" :key="'modality-' + idx">
            <v-chip small class="meta-chip">{{ item }}</v-chip><span
              v-if="idx < getArray(previewDetails.Modality).length - 1">,</span>
          </span>
        </div>

        <div class="meta-row">
          <span class="meta-title">Type:</span>
          <span v-for="(item, idx) in getArray(previewDetails.Type)" :key="'type-' + idx">
            <v-chip small class="meta-chip">{{ item }}</v-chip><span
              v-if="idx < getArray(previewDetails.Type).length - 1">,</span>
          </span>
        </div>

        <div class="meta-row">
          <span class="meta-title">Language:</span>
          <span v-for="(item, idx) in getArray(previewDetails.Language)" :key="'lang-' + idx">
            <v-chip small class="meta-chip">{{ item }}</v-chip><span
              v-if="idx < getArray(previewDetails.Language).length - 1">,</span>
          </span>
        </div>

        <div class="meta-row">
          <span class="meta-title">Occurrence:</span>
          <v-chip small class="meta-chip">{{ previewDetails.OccurrenceType }}</v-chip>
        </div>
      </v-card>

      <!-- Timing Info -->
      <v-card outlined class="mb-4 pa-3 info-section">
        <div class="info-grid">
          <div class="info-label">Time Zone</div>
          <div class="info-value">{{ previewDetails.TimeZone || '—' }}</div>

          <div class="info-label">Event Date</div>
          <div class="info-value">{{ formatDate(previewDetails.EventDate) }}</div>

          <div class="info-label">Start Time</div>
          <div class="info-value">{{ formatDateTime(previewDetails.TimeStart) }}</div>

          <div class="info-label">End Time</div>
          <div class="info-value">{{ formatDateTime(previewDetails.TimeEnd) }}</div>
        </div>
      </v-card>

      <!-- Location -->
      <v-card outlined class="mb-4 pa-3 info-section">
        <div class="d-flex align-center mb-2">
          <v-icon color="primary" class="mr-2">mdi-map-marker</v-icon>
          <div class="section-title">Location</div>
        </div>
        <div class="info-grid">
          <template v-if="previewDetails.EventLocationType === 1">
            <div class="info-label">Title</div>
            <div class="info-value">{{ previewDetails.LocationTitle || '—' }}</div>

            <div class="info-label">Address</div>
            <div class="info-value">{{ previewDetails.LocationAddress || '—' }}</div>
          </template>
          <template v-else>
            <div class="info-label">Link</div>
            <div class="info-value">
              <a :href="previewDetails.UrlLink" target="_blank" class="text-link">
                {{ previewDetails.UrlLink }}
                <v-icon small class="ml-1">mdi-open-in-new</v-icon>
              </a>
            </div>
          </template>
        </div>
      </v-card>

      <!-- Host Info -->
      <v-card outlined class="mb-4 pa-3 info-section">
        <div class="d-flex align-center mb-2">
          <v-icon color="primary" class="mr-2">mdi-account</v-icon>
          <div class="section-title">Host</div>
        </div>
        <div class="contact-grid">
          <v-icon small class="contact-icon">mdi-account-outline</v-icon>
          <div>{{ previewDetails.HostName || '—' }}</div>

          <v-icon small class="contact-icon">mdi-email-outline</v-icon>
          <div>
            <a v-if="previewDetails.HostEmail" :href="`mailto:${previewDetails.HostEmail}`" class="text-link">
              {{ previewDetails.HostEmail }}
            </a>
            <span v-else>—</span>
          </div>

          <v-icon small class="contact-icon">mdi-phone-outline</v-icon>
          <div>{{ previewDetails.HostPhoneNumber || '—' }}</div>
        </div>
      </v-card>

      <!-- Description -->
      <v-card outlined class="pa-3 info-section" v-if="previewDetails.Description">
        <div class="d-flex align-center mb-2">
          <v-icon color="primary" class="mr-2">mdi-text</v-icon>
          <div class="section-title">Description</div>
        </div>
        <div class="description-text">
          {{ previewDetails.Description }}
        </div>
      </v-card>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "EventPreview",
  props: {
    Previewdialog: Boolean,
    previewDetails: Object,
  },
  methods: {
    getArray(field) {
      if (!field) return [];
      return typeof field === "string"
        ? field.split(",").map((s) => s.trim())
        : Array.isArray(field)
          ? field
          : [field];
    },
    formatDate(dateStr) {
      if (!dateStr) return "—";
      const options = {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      };
      return new Date(dateStr).toLocaleDateString("en-US", options);
    },
    formatDateTime(dateTimeStr) {
      if (!dateTimeStr) return "—";
      const options = {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      };
      return new Date(dateTimeStr).toLocaleString("en-US", options);
    },
  },
};
</script>

<style scoped>
.preview-card {
  border-radius: 10px;
  background-color: #fff;
}

.previewHeading {
  font-weight: 700;
  font-size: 18px;
  color: #333;
}

.event-id {
  font-size: 13px;
  color: #777;
}

.section-title {
  font-weight: 600;
  font-size: 15px;
  color: #333;
}

.info-section {
  border-radius: 6px;
  font-size: 14px;
}

.info-grid {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 8px 12px;
}

.info-label {
  font-weight: 500;
  color: #666;
}

.info-value {
  color: #333;
  word-break: break-word;
}

.contact-grid {
  display: grid;
  grid-template-columns: 20px 1fr;
  row-gap: 8px;
  column-gap: 10px;
  align-items: center;
}

.contact-icon {
  color: #888;
}

.description-text {
  color: #444;
  white-space: pre-line;
  line-height: 1.5;
}

.text-link {
  color: #1976D2;
  text-decoration: none;
}

.text-link:hover {
  text-decoration: underline;
}

.meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
  margin-bottom: 8px;
}

.meta-title {
  font-weight: 600;
  color: #444;
  margin-right: 4px;
}

.meta-chip {
  font-size: 13px;
  background-color: #f0f0f0;
  color: #333;
}
</style>
